import { useState } from "react";
import DefaultImg from "../../../../assets/img/default-image.svg";
import Roles from "../../../../models/roles";
import {
  formatAllowance,
  VACANCY_CANDIDATE_VISIBILITY,
  VACANCY_SELECTION_METHOD,
} from "../../../../models/vacancy";
import { useAppSelector } from "../../../../store";
import { TVacancy } from "../../../../store/vacancy/types";
import ViewShiftNotes from "../../../Candidate/Jobs/AcceptedListing/ViewShiftNotes";
import CustomButton from "../../../UI/Buttons/CustomButton";
import EditPencil from "../../../UI/Icons/EditPencil";
import Warning from "../../../UI/Icons/Warning";
import VacancyPosition from "../../../UI/VacancyPosition";
import { DisplayTimeframe } from "../../../Vacancy/timeframeApiHelper";

const JobOverview = ({
  selectedVacancyDetails,
  formattedTimeframes,
  onEdit,
}: {
  selectedVacancyDetails: TVacancy | null;
  formattedTimeframes: DisplayTimeframe[] | undefined;
  onEdit: () => void;
}) => {
  const {
    centre,
    status,
    vacancy_current_status,
    vacancy_positions,
    alternate_position_legacy,
    visibility_type,
    visibility_upgraded,
    posted,
  } = selectedVacancyDetails || {};
  const selectionMethod =
    selectedVacancyDetails?.selection_method ===
    VACANCY_SELECTION_METHOD.FIRST_COME_FIRST_SERVED
      ? "First To Accept"
      : "I Choose the Candidate";
  const isExpired = vacancy_current_status === "expired";
  const isFilled = status === "filled";
  const isActive = status === "active";
  const user = useAppSelector((state) => state.auth.user);

  const shiftNotes = useAppSelector(
    (state) => state.vacancy.selectedVacancy?.shift_notes
  );

  const [showShiftNotesModal, setShowShiftNotesModal] =
    useState<boolean>(false);

  const handleShiftNotesPopup = () => {
    setShowShiftNotesModal(true);
  };

  const onClickClose = () => {
    setShowShiftNotesModal(false);
  };

  const totalApplication = selectedVacancyDetails?.vacancy_applications?.total || 0;
  const isCandidateAvailable = selectedVacancyDetails?.selected_candidate?.candidate_available;
  
  return (
    <>
      <ViewShiftNotes
        showShiftNotesModal={showShiftNotesModal}
        onClickClose={onClickClose}
        notes={shiftNotes ?? ""}
      />

      <div className="bg-teal-pale border border-grey-light radius-8px p-15px d-flex flex-column flex-lg-row gap-30 gap-mobile-10 align-items-lg-center text-break">
        <div className="flex-grow-1">
          <div className="flex-grow-1">
            <div className="d-flex gap-20 align-items-center">
              <div className="w-50px h-50px  radius-50px">
                <img
                  alt="centre"
                  width="50px"
                  height="50px"
                  className="img-responsive rounded-circle object-fit-cover"
                  src={centre?.photo || DefaultImg}
                />
              </div>
              <div className="d-flex flex-column gap-3 max-w-500px">
                <div className="fs-16 fw-700 text-black">
                  <VacancyPosition
                    positions={vacancy_positions}
                    alternatePosition={alternate_position_legacy || ""}
                    fullVacancyName={true}
                    removeLineBreaks={true}
                  />
                </div>
                <div className="fs-13 fw-400 text-black">
                  {`${centre?.business_name}, ${centre?.address}`}
                </div>
              </div>
            </div>
          </div>
        </div>
        {!isExpired && (
          <div className="d-flex">
            <div
              className={`border py-4px px-8px radius-8px fs-13 text-nowrap align-items-center ${
                isActive
                  ? "border-alert text-alert"
                  : isFilled
                  ? "border-teal-dark text-teal-dark"
                  : ""
              }`}
            >
              {isActive ? "Pending" : isFilled ? "Accepted" : ""}
            </div>
          </div>
        )}
        <div className="d-flex flex-column gap-5 text-break">
          <div className="fs-10 fw-400 text-grey-medium">
            Requested Timeframe
          </div>
          <div className="fs-13 fw-400 text-black">
            {formattedTimeframes &&
              formattedTimeframes?.map((ftf, i) => (
                <div
                  key={i}
                  className={`fs-13 ${
                    ftf.alert ? "text-alert fw-700" : "fw-400"
                  }`}
                >
                  {ftf.label}
                </div>
              ))}
          </div>
        </div>
        <div className="d-flex flex-column gap-5 text-nowrap">
          <div className="fs-10 fw-400 text-grey-medium">Posted</div>
          <div className="fs-13 fw-400 text-black">{posted}</div>
        </div>
        {(visibility_type !== VACANCY_CANDIDATE_VISIBILITY.SELECTED_CANDIDATE ||
          (visibility_type ===
            VACANCY_CANDIDATE_VISIBILITY.SELECTED_CANDIDATE &&
            visibility_upgraded)) && (
          <div className="d-flex flex-column gap-5 text-nowrap">
            <div className="fs-10 fw-400 text-grey-medium">
              Chosen Candidate
            </div>
            <div className="fs-13 fw-400 text-black">{selectionMethod}</div>
          </div>
        )}
        {
          selectedVacancyDetails?.allowance !== undefined &&
          selectedVacancyDetails?.allowance !== null &&
          Number(selectedVacancyDetails?.allowance) > 0 && (user?.role?.name === Roles.SUPER_ADMIN || user?.role?.name === Roles.GENERAL_ADMIN) && (
            <div className="d-flex flex-column gap-5 text-nowrap">
              <div className="fs-10 fw-400 text-grey-medium">Daily Allowance</div>
              <div className="fs-13 fw-400 text-black">{formatAllowance(Number(selectedVacancyDetails?.allowance), true)}</div>
            </div>
          )
        }        
        {shiftNotes !== "" && (
          <div className="cursor-pointer" onClick={handleShiftNotesPopup}>
            <Warning />
          </div>
        )}

        {/*
         * Show edit button if vacancy is not expired, not filled, and has no applications
         */}
        {!isExpired && !isFilled && totalApplication === 0 && isCandidateAvailable !== false && (
          <CustomButton
            variant={"primary"}
            type="button"
            className="btn-theme bg-teal text-black text-nowrap"
            //   onClick={() => handleEditVacancy(selectedVacancyDetails)}
            onClick={onEdit}
          >
            <EditPencil />
            Edit
          </CustomButton>
        )}
      </div>
    </>
  );
};

export default JobOverview;
